import { useCallback, useRef, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useOrder } from '../../../../Providers/OrderProvider'

/**
 * DK : Hook do dodawania załączników (pdf, txt, docx)
 *
 * @param {Object} params
 * @param {Array<string>} [params.allowedExtensions=['.pdf','.txt','.docx']] - dopuszczalne rozszerzenia
 * @param {Array<string>} [params.allowedFileTypes=['application/pdf','text/plain','application/vnd.openxmlformats-officedocument.wordprocessingml.document']] - dopuszczalne typy 
 * @param {number} [params.maxFileSize] - maksymalny rozmiar pliku
 */
const useAttachmentUploader = ({
    allowedExtensions = ['.pdf', '.txt', '.docx'],
    allowedFileTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    maxFileSize = 5 * 1024 * 1024, // 5 MB
} = {}) => {

    const rootRef = useRef < HTMLDivElement > (null)
    const { setOrderInfo, orderInfo } = useOrder()
    const fileInputRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)

    useEffect(() => {
        rootRef.current?.focus()
    }, [])

    const validateFile = useCallback(
        (file) => {
            if (!file) {
                return false
            }
            if (orderInfo.attachments.length >= 5) {
                toast.warning('Można dodać max 5 plików!')
                return false
            }

            const { type: fileType, name: fileName, size } = file
            const lowerName = fileName.toLowerCase()
            const extension = lowerName.substring(lowerName.lastIndexOf('.'))

            if (size > maxFileSize) {
                toast.warning(`Plik jest za duży (maksymalnie ${maxFileSize / (1024 * 1024)} MB)`)
                return false
            }

            if (allowedFileTypes.includes(fileType) && allowedExtensions.includes(extension)) {
                return true
            } else {
                toast.warning(`Nieprawidłowy typ pliku. Dopuszczalne: ${allowedExtensions.join(', ')}`)
                return false
            }
        },
        [allowedExtensions, allowedFileTypes, maxFileSize]
    )

    // const addFileToOrder = useCallback(
    //     (file) => {
    //         const existing = Array.isArray(orderInfo.attachments) ? orderInfo.attachments : []
    //         const newAttachments = [...existing, file]

    //         setOrderInfo((prev) => ({
    //             ...prev,
    //             attachments: newAttachments,
    //         }))
    //     },
    //     [orderInfo.attachments, setOrderInfo]
    // )

    const addFileToOrder = useCallback(
        (file) => {
            const existingNames = Array.isArray(orderInfo.attachments)
                ? orderInfo.attachments.map(f => f.name)
                : []

            const idx = file.name.lastIndexOf('.')
            const base = idx > 0 ? file.name.slice(0, idx) : file.name
            const ext = idx > 0 ? file.name.slice(idx) : ''

            let finalName = file.name;
            if (existingNames.includes(finalName)) {
                let counter = 1;
                do {
                    finalName = `${base}_${counter}${ext}`;
                    counter++;
                }
                while (existingNames.includes(finalName)) {
                    file = new File([file], finalName, { type: file.type })
                }
            }

            setOrderInfo(prev => ({
                ...prev,
                attachments: [...prev.attachments, file],
            }))
        },
        [orderInfo.attachments, setOrderInfo]
    )

    const processFile = useCallback(
        (file) => {
            if (validateFile(file)) {
                addFileToOrder(file)
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                }
            }
        },
        [validateFile, addFileToOrder]
    )

    const handleFileChange = useCallback(
        (e) => {
            const file = e.target.files && e.target.files[0]
            if (file) {
                processFile(file)
            }
        },
        [processFile]
    )

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDragging(false)
            const file = e.dataTransfer.files && e.dataTransfer.files[0]
            if (file) {
                processFile(file)
                if (fileInputRef.current) {
                    fileInputRef.current.files = e.dataTransfer.files
                }
            }
        },
        [processFile]
    )

    const handleDragOver = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!isDragging) setIsDragging(true)
        },
        [isDragging]
    )

    const handleDragLeave = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isDragging) setIsDragging(false)
        },
        [isDragging]
    )

    const handleSelectFile = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }, [])

    const getRootProps = useCallback(
        () => ({
            ref: rootRef,
            onClick: handleSelectFile,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
            onPaste: handlePaste,
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') handleSelectFile()
            },
            style: {
                cursor: 'pointer',
                border: isDragging ? '2px dashed #007bff' : '2px dashed #ccc',
                padding: '1rem',
                borderRadius: '4px',
                textAlign: 'center',
                background: isDragging ? '#f0f8ff' : 'transparent'
            }
        }),
        [handleSelectFile, handleDragOver, handleDragLeave, handleDrop, isDragging]
    )

    const getInputProps = useCallback(
        () => ({
            type: 'file',
            ref: fileInputRef,
            onChange: handleFileChange,
            accept: allowedExtensions.join(','),
            style: { display: 'none' },
        }),
        [handleFileChange, allowedExtensions]
    )

    const handlePaste = useCallback(
        (e) => {
            const items = e.clipboardData?.files
            if (items && items.length > 0) {
                const file = items[0]
                processFile(file)
            }
        },
        [processFile]
    )

    return {
        isDragging,
        getRootProps,
        getInputProps,
    }
}

export default useAttachmentUploader
